import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { useEffect, useState } from "react";
import { apiRequestWrapper } from "../../../../../api/shared/http-client";
import { appLog } from "../../../../../context";
import { useAppStore } from "../../../../../store/app.store";
import OurDropdown from "../../../utils/OurDropdown/OurDropdown";
import Spinner from "../../../utils/Spinner/Spinner";

import {
  fetchSettings,
  updateSettingsDefaultReference,
} from "../../../../../api/application/settings";
import { fetchTables } from "../../../../../api/application/table";
import type { GeneratedReference } from "../../../../../api/infrastructure/reference.formatter";
import type { APITable } from "../../../../../api/infrastructure/table.formatter";
import { showToast } from "../../../../shared/global-tools/toast-system";
import "./DefineDefaultReference.scss";

export default function DefineDefaultReference({
  generatedReferences,
}: { generatedReferences: GeneratedReference[] }) {
  const wrapAsync = useWrapAsync();
  const [loading, setLoading] = useState(false);
  const { settings, setSettings } = useAppStore();
  const [selectedReferenceId, setSelectedReferenceId] = useState<number>();
  const [selectedTableId, setSelectedTableId] = useState<number>();
  const [tables, setTables] = useState<APITable[]>();

  const changeDefaultReference = wrapAsync(async () => {
    if (selectedReferenceId === undefined || selectedTableId === undefined) return;
    setLoading(true);
    try {
      await updateSettingsDefaultReference({
        referenceId: selectedReferenceId,
        tableId: selectedTableId,
      });
      setSettings(undefined);
      showToast("Définition référentiel réussie", { severity: "success" });
      setSelectedReferenceId(undefined);
      setSelectedTableId(undefined);
    } catch (error: any) {
      appLog.error(error);
      showToast(error.message, { severity: "error", sticky: false });
    }
    setLoading(false);
  });

  useEffect(
    wrapAsync(async () => {
      if (settings) return;
      await apiRequestWrapper(
        async () => {
          setSettings(await fetchSettings());
        },
        { handle404: true },
      );
    }),
    [settings],
  );
  useEffect(
    wrapAsync(async () => {
      if (!selectedReferenceId) return;
      await apiRequestWrapper(async () => {
        const result = await fetchTables(selectedReferenceId);
        setTables(result);
      });
    }),
    [selectedReferenceId],
  );

  if (!settings) return <Spinner />;
  return (
    <div className="Card DefineDefaultReference">
      <h2 className="DefineDefaultReference-title">Définir le référentiel par défaut</h2>
      <div className="DefineDefaultReference-content">
        <div className="DefineDefaultReference-reference">
          <span className="DefineDefaultReference-referenceLabel">Référentiel actif</span>
          <span className="DefineDefaultReference-referenceValue">
            {settings.defaultReference
              ? settings.defaultReference.name
              : "Aucun référentiel par défaut défini"}
          </span>
        </div>

        <div className="DefineDefaultReference-form">
          <label className="Field">
            <span className="Field-label">Sélectionner un référentiel</span>
            <OurDropdown<number>
              selectedOption={selectedReferenceId}
              initialOptions={generatedReferences.map((d) => ({
                label: d.name,
                value: d.id,
              }))}
              onChange={(value) => {
                setSelectedReferenceId(value);
                setSelectedTableId(undefined);
              }}
              filter
            />
          </label>

          <label className="Field">
            <span className="Field-label">Sélectionner une table</span>
            <OurDropdown<number>
              selectedOption={selectedTableId}
              initialOptions={tables?.map((d) => ({
                label: d.name,
                value: d.id,
              }))}
              onChange={(value) => {
                setSelectedTableId(value);
              }}
              filter
            />
          </label>

          <Button
            className="Btn success mt-3"
            label="Valider"
            onClick={changeDefaultReference}
            disabled={selectedReferenceId === undefined}
            loading={loading}
          />
        </div>

        <div
          style={{
            color: "#ADADAD",
            fontSize: "12px",
            fontWeight: "500",
            textAlign: "justify",
          }}
        >
          Cette section permet aux administrateurs de configurer le référentiel par défaut du
          tableau de board. Le référentiel par défaut est utilisé comme source de donnée principale
        </div>
      </div>
    </div>
  );
}
